import { createContext, useContext, useMemo } from 'react';

import { type BuilderView, type BuilderViewSourceSchema } from '@/types/schema/BuilderView';
import { type KnackObject } from '@/types/schema/KnackObject';
import { useApplicationQuery } from '@/hooks/api/queries/useApplicationQuery';

type ActiveViewContextState<T extends BuilderView> = {
  view: T;

  // If the view has a `source` property, the `sourceObject` will be of type KnackObject. Otherwise, it will be null
  sourceObject: T extends { source: BuilderViewSourceSchema } ? KnackObject : null;
};

type ActiveViewContextProviderProps<T extends BuilderView> = {
  view: T;
  children: React.ReactNode;
};

const ActiveViewContext = createContext<ActiveViewContextState<BuilderView> | null>(null);

export function ActiveViewContextProvider<T extends BuilderView>({
  view,
  children
}: ActiveViewContextProviderProps<T>) {
  const { data: application } = useApplicationQuery();

  let sourceObject: KnackObject | null = null;

  // Check if the view has a 'source' property
  if ('source' in view) {
    sourceObject = application?.objects.find((object) => object.key === view.source.object) || null;
  }

  const contextValue = useMemo(() => ({ view, sourceObject }), [view, sourceObject]);

  return <ActiveViewContext.Provider value={contextValue}>{children}</ActiveViewContext.Provider>;
}

export const useActiveViewContext = <T extends BuilderView>() => {
  const context = useContext(ActiveViewContext);

  if (!context) {
    throw new Error('useActiveViewContext must be used within an ActiveViewContextProvider');
  }

  return context as ActiveViewContextState<T>;
};
